import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Breadcrumbs, Container, Section,
} from "../components/Primitives";
import { ProductGrid, ProductGridSkeleton } from "../components/ProductCard";
import { Ornament } from "../components/Ornament";
import { getProducts } from "../lib/api";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { Reveal } from "../lib/motion";
import { Link } from "react-router-dom";

/**
 * Semi-Precious Collection — launched 15 Oct 2026.
 *
 * The owner curates this herself: titles and spec lines are hand-written,
 * images are AI stand-ins marked "Representative image", and prices are
 * published one by one as she sets them. Until a product has a price it
 * shows "Price on request" and the primary CTA routes to /contact.
 */
export default function CollectionSemiPrecious() {
  useSEO({
    title: "Semi-Precious Collection — Shreem Mantra Energy",
    description:
      "A small, hand-picked collection of malas, bracelets, selenite and raw stones. Each piece is representative — real photographs replace the images before dispatch.",
    canonical: "/collection/semi-precious",
    jsonLd: breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Semi-Precious Collection", url: "/collection/semi-precious" },
    ]),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["products", { type: "semi-precious" }],
    queryFn: () => getProducts({ type: "semi-precious", limit: 48 }),
  });

  const products = data?.items || [];

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs
          trail={[
            { label: "Home", to: "/" },
            { label: "Semi-Precious Collection" },
          ]}
        />
      </Container>

      <Section className="!pt-10 !pb-6">
        <Container>
          <Reveal className="max-w-3xl">
            <Ornament className="mb-6" />
            <p className="eyebrow-wide">The Semi-Precious Collection</p>
            <h1
              className="mt-4 text-[32px] leading-tight tracking-[-0.01em] sm:text-[42px]"
              style={{ fontFamily: "Fraunces, Georgia, serif" }}
            >
              Malas, bracelets, selenite and raw stones — curated in small numbers
            </h1>
            <p className="mt-5 max-w-[var(--measure-read)] text-[15px] leading-[1.75] text-dim">
              A deliberately small collection, separate from the certified gemstones and rudraksha
              that make up most of the catalogue. Each piece has a spec line, a stock count and a
              representative image — the real photograph of the piece you'll receive is added
              before it leaves our workshop. Prices for several items are being finalised; message
              us for the current rate on any piece marked "Price on request".
            </p>
          </Reveal>
        </Container>
      </Section>

      <Section className="!pt-6">
        <Container>
          {isLoading && <ProductGridSkeleton count={8} testId="semi-precious-skeleton" />}

          {isError && (
            <div className="border border-rule p-6" data-testid="semi-precious-error">
              <p className="text-base text-ink">
                The collection didn't load just now. Refresh the page, or{" "}
                <Link to="/contact" className="link-u">write to us</Link> for anything you were
                looking for.
              </p>
            </div>
          )}

          {!isLoading && !isError && products.length === 0 && (
            <div className="border border-rule p-10 text-center" data-testid="semi-precious-empty">
              <p className="eyebrow-wide">Coming soon</p>
              <p className="mx-auto mt-4 max-w-[var(--measure-read)] text-[15px] leading-[1.7] text-dim">
                We're preparing this collection for launch. In the meantime, message us about any
                specific piece you're looking for and we'll let you know when it goes live.
              </p>
              <Link to="/contact" className="btn btn-secondary mt-6" data-testid="semi-precious-contact">
                Ask us about a piece
              </Link>
            </div>
          )}

          {!isLoading && !isError && products.length > 0 && (
            <ProductGrid products={products} testId="semi-precious-grid" />
          )}
        </Container>
      </Section>
    </>
  );
}
